import { AdminUserEvent } from 'tools/bff/generated/bff/admin/admin_user_pb';
import { Box, Loader, Text } from '@tocktix/pantry';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { sortBy } from 'es-toolkit/compat';
import { userClient } from 'tools/bff/client';
import UserHistoryEventCard from 'tools/users/UserHistoryEventCard';

type UserHistorySectionProps = {
  gdusUuid?: string;
  enabled: boolean;
};

const formatDateTime = (dateTime?: string): string => {
  if (!dateTime) {
    return '\u2014';
  }

  const parsedDate = new Date(dateTime);
  if (Number.isNaN(parsedDate.getTime())) {
    return '\u2014';
  }

  return parsedDate.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const UserHistorySection: FunctionComponent<UserHistorySectionProps> = ({ gdusUuid, enabled }) => {
  const [userHistory, setUserHistory] = useState<AdminUserEvent[]>([]);
  const [userHistoryLoading, setUserHistoryLoading] = useState(false);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !gdusUuid || hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;

    let isMounted = true;
    const loadHistory = async () => {
      setUserHistoryLoading(true);
      try {
        const res = await userClient.getUserHistory({ gdusUuid });
        if (isMounted) {
          setUserHistory(res.events);
        }
      } catch {
        // User history fetch failure is non-fatal; leave the list empty.
      } finally {
        if (isMounted) {
          setUserHistoryLoading(false);
        }
      }
    };

    loadHistory();
    return () => {
      isMounted = false;
    };
  }, [enabled, gdusUuid]);

  const sortedUserHistory = sortBy(userHistory, 'event.value.dateTime');
  const renderHistoryEventCard = (historyEvent: AdminUserEvent) => {
    const dateTime = formatDateTime(historyEvent.event.value?.dateTime);

    switch (historyEvent.event.case) {
      case 'resyUserCreated': {
        return (
          <UserHistoryEventCard
            title="Resy user created"
            id={historyEvent.event.value.userId.toString()}
            icon="success"
            dateTime={dateTime}
          />
        );
      }
      case 'tockPatronCreated': {
        return (
          <UserHistoryEventCard
            title="Tock patron created"
            id={historyEvent.event.value.patronId.toString()}
            icon="success"
            dateTime={dateTime}
          />
        );
      }
      case 'gdusUserCreated': {
        return (
          <UserHistoryEventCard
            title="GDUS user created"
            id={historyEvent.event.value.gdusUuid}
            icon="success"
            dateTime={dateTime}
          />
        );
      }
      case 'gdusUserDeactivated': {
        const { reason, updatedByTockPatronId } = historyEvent.event.value;
        return (
          <UserHistoryEventCard
            title="GDUS user deactivated"
            id={historyEvent.event.value.gdusUuid}
            icon="danger"
            dateTime={dateTime}
            reason={reason}
            updatedByTockPatronId={updatedByTockPatronId}
          />
        );
      }
      case 'gdusUserReactivated': {
        const { reason, updatedByTockPatronId } = historyEvent.event.value;
        return (
          <UserHistoryEventCard
            title="GDUS user reactivated"
            icon="info"
            dateTime={dateTime}
            reason={reason}
            updatedByTockPatronId={updatedByTockPatronId}
          />
        );
      }
      case 'gdusUserDeleted': {
        const { deletedByTockPatronId, source, gdusUuid } = historyEvent.event.value;
        return (
          <UserHistoryEventCard
            title="GDUS user deleted"
            id={gdusUuid}
            icon="danger"
            dateTime={dateTime}
            updatedByTockPatronId={deletedByTockPatronId}
            source={source}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <Box style={{ maxHeight: '400px', overflowY: 'auto' }}>
      {userHistoryLoading ? (
        <Box padding={300} display="flex" justifyContent="center">
          <Loader aria-label="user-history-loading" size="md" />
        </Box>
      ) : sortedUserHistory.length === 0 ? (
        <Box padding={300}>
          <Text variant="body" color="fgSubtle">
            No user history available.
          </Text>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={200} padding={200}>
          {sortedUserHistory.map((historyEvent, index) => (
            <Box key={`history-event-${index}`}>{renderHistoryEventCard(historyEvent)}</Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default UserHistorySection;
